import { summarizeRows, type WorkItem, type WorkList } from './work-list-view';

// #3549: changes made on the Web list, shown before the server's list has
// them. What the list shows is the last list read with these laid over it, so
// rows and counts move as soon as a button is pressed and go back together
// when the save fails. A change is kept until a read started after its save
// lands, so a read already running cannot bring the old row back.

/** What a leaving row is found by. */
export type WorkLeaveBy = 'goal' | 'subtask' | 'candidate' | 'completion' | 'reopen' | 'close_proposal' | 'hold';

export type WorkListChange =
  /** Rows leave the list: a goal completed, a candidate or proposal answered, a task dropped. */
  | { type: 'leave'; by: WorkLeaveBy; id: string }
  /** A task's or goal's check shows done. */
  | { type: 'check'; id: string }
  /** A judgment row shows the option chosen. */
  | { type: 'choose'; key: string; optionKey: string }
  /** A provisional deadline (#3064) becomes the principal's own. */
  | { type: 'deadline'; goalId: string; day: string };

export interface PendingWorkChange {
  changes: WorkListChange[];
  /** Leaving rows still play their exit; they leave the list once it has played. */
  exiting: boolean;
  /** How many reads of the list had started when the save went through; null while it runs. */
  savedAt: number | null;
}

function rowLeaves(change: WorkListChange, item: WorkItem): boolean {
  if (change.type !== 'leave') return false;
  switch (change.by) {
    case 'goal': return item.goal_id === change.id && !item.candidate_id;
    case 'subtask': return item.subtask_id === change.id;
    case 'candidate': return item.candidate_id === change.id;
    case 'completion': return item.completion_id === change.id;
    case 'reopen': return item.reopen_id === change.id;
    case 'close_proposal': return item.close_proposal_subtask_id === change.id;
    case 'hold': return item.goal_link?.hold_id === change.id;
  }
}

function withDeadline(item: WorkItem, day: string, today: string): WorkItem {
  return {
    ...item,
    due: { day, time: null },
    overdue: day < today,
    note: item.note?.code === 'provisional_deadline' ? null : item.note,
  };
}

/** The list as shown: the last list read with the changes not yet in it laid over it. */
export function applyWorkChanges(list: WorkList, pending: Iterable<PendingWorkChange>): WorkList {
  const entries = [...pending];
  if (entries.length === 0) return list;
  let changed = false;
  const items: WorkItem[] = [];
  for (const item of list.items) {
    if (entries.some((entry) => !entry.exiting && entry.changes.some((change) => rowLeaves(change, item)))) {
      changed = true;
      continue;
    }
    let shown = item;
    for (const entry of entries) {
      for (const change of entry.changes) {
        if (change.type === 'deadline' && change.goalId === item.goal_id && item.due?.provisional) {
          shown = withDeadline(shown, change.day, list.today);
        }
      }
    }
    if (shown !== item) changed = true;
    items.push(shown);
  }
  return changed ? { ...list, items, summary: summarizeRows(items) } : list;
}

/** Ids of tasks and goals shown checked: checked ones, and goals still playing their exit. */
export function checkedIds(pending: Iterable<PendingWorkChange>): Set<string> {
  const ids = new Set<string>();
  for (const entry of pending) {
    for (const change of entry.changes) {
      if (change.type === 'check') ids.add(change.id);
      else if (change.type === 'leave' && change.by === 'goal' && entry.exiting) ids.add(change.id);
    }
  }
  return ids;
}

/** Keys of the rows playing their exit. */
export function leavingKeys(list: WorkList | null, pending: Iterable<PendingWorkChange>): Set<string> {
  const keys = new Set<string>();
  if (!list) return keys;
  const exiting = [...pending].filter((entry) => entry.exiting);
  if (exiting.length === 0) return keys;
  for (const item of list.items) {
    if (exiting.some((entry) => entry.changes.some((change) => rowLeaves(change, item)))) keys.add(item.key);
  }
  return keys;
}

/** Judgment rows just answered: row key → option key. */
export function chosenOptions(pending: Iterable<PendingWorkChange>): Map<string, string> {
  const chosen = new Map<string, string>();
  for (const entry of pending) {
    for (const change of entry.changes) {
      if (change.type === 'choose') chosen.set(change.key, change.optionKey);
    }
  }
  return chosen;
}

/** Drops the changes a read has: those saved before the read numbered `readNumber` started. */
export function settleWorkChanges(pending: Map<number, PendingWorkChange>, readNumber: number) {
  for (const [id, entry] of pending) {
    if (entry.savedAt !== null && readNumber > entry.savedAt) pending.delete(id);
  }
}
